import React, { useEffect, useState } from "react";
import { Box, Stack } from "@mui/material";
import SubscriptionBanner from "./Banners/SubscriptionBanner";
import LoyaltyPromoBanner from "./Banners/LoyaltyPromoBanner";
import InstallAppBanner from "./Banners/InstallAppBanner";
import MonthSummaryBanner from "./Banners/MonthSummaryBanner";
import EarningsGoalBanner from "./Banners/EarningsGoalBanner";
import HourlyRateBanner from "./Banners/HourlyRateBanner";
import WeekAtAGlanceBanner from "./Banners/WeekAtAGlanceBanner";
import LostRevenueBanner from "./Banners/LostRevenueBanner";
import TimeBalanceBanner from "./Banners/TimeBalanceBanner";
import { useCookies } from "react-cookie";
import SetupWizard from "./SetupWizard";
import Brand from "../../api/Brand";
import { Overview } from "../../api/EarningsOverview";
import { ANALYTICS_ENABLED } from "../../constants/features";

interface DashboardProps {
    brand?: Brand;
    overview?: Overview;
}

const Dashboard = ({ brand: brandProp, overview }: DashboardProps) => {
    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [brandLocal, setBrandLocal] = useState<Brand | undefined>();

    const brand = brandProp ?? brandLocal;

    useEffect(() => {
        if (brandProp) return;
        Brand.info(uuid, brand_id).then((b) => {
            setBrandLocal(b);
        });
    }, [uuid, brand_id, brandProp]);

    // XOR gate: show the earnings zone once the master has real traction
    // (≥3 lifetime non-cancelled bookings) OR has finished setup; otherwise the
    // white sheet leads with the SetupWizard. Mutually exclusive.
    const steps = brand?.setupSteps;
    const setupComplete =
        !!steps &&
        !!steps.branding &&
        !!steps.wallpaper &&
        !!steps.services &&
        !!steps.location &&
        !!steps.profileInfo &&
        !!steps.loyalty;
    const showEarnings =
        ANALYTICS_ENABLED &&
        ((overview?.lifetimeActiveBookings ?? 0) >= 3 || setupComplete);

    // Earnings zone — order + per-widget visibility per FEATURE-002.
    const earningsZone = (
        <>
            {overview?.month && (
                <Box className="stagger-child" width="100%">
                    <MonthSummaryBanner summary={overview.month} />
                </Box>
            )}
            {/* Goal progress — only when a goal is set. */}
            {overview?.monthlyRevenueGoal ? (
                <Box className="stagger-child" width="100%">
                    <EarningsGoalBanner overview={overview} />
                </Box>
            ) : null}
            {/* Self-hide (null, no DOM node) when 0 hours — no wrapper Box. */}
            <HourlyRateBanner overview={overview} />
            {overview?.week?.days && (
                <Box className="stagger-child" width="100%">
                    <WeekAtAGlanceBanner overview={overview} />
                </Box>
            )}
            {/* Both self-hide when nothing to show — no wrapper Box. */}
            <LostRevenueBanner />
            <TimeBalanceBanner overview={overview} />
        </>
    );

    return (
        <Stack
            className="force-dynamic-translation"
            direction="column"
            width="100%"
            justifyContent="flex-start"
            alignItems="center"
            spacing={2}
        >
            {/* InstallAppBanner is always first. */}
            <Box className="stagger-child" width="100%">
                <InstallAppBanner />
            </Box>

            {/* Onboarding mode: SetupWizard sits above the promo banners. */}
            {!showEarnings && brand && (
                <Box className="stagger-child" width="100%">
                    <SetupWizard
                        brandConfigured={!!brand.setupSteps.branding}
                        wallpaperConfigured={!!brand.setupSteps.wallpaper}
                        servicesConfigured={!!brand.setupSteps.services}
                        locationConfigured={!!brand.setupSteps.location}
                        profileConfigured={!!brand.setupSteps.profileInfo}
                        loyaltyConfigured={!!brand.setupSteps.loyalty}
                    />
                </Box>
            )}

            <Box className="stagger-child" width="100%">
                <SubscriptionBanner />
            </Box>
            <Box className="stagger-child" width="100%">
                <LoyaltyPromoBanner />
            </Box>

            {/* Active mode: earnings zone at the bottom. */}
            {showEarnings && earningsZone}
        </Stack>
    );
};

export default Dashboard;
